import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus } from "lucide-react";

import { tr, lbl } from "../lib/i18n";
const STAGES = ["New", "Contacted", "Interested", "Negotiating", "Test Drive Done", "Won", "Lost", "Dormant"];

export default function CustomersPage() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(""); const [stage, setStage] = useState("");
  const [pipeline, setPipeline] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY = { name: "", mobile: "", email: "", customer_type: "Buyer", source: "Walk-in", stage: "New" };
  const [form, setForm] = useState(EMPTY);
  const [existing, setExisting] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = (c) => c.follow_up_date && c.follow_up_date.slice(0, 10) < today && !["Won", "Lost", "Dormant"].includes(c.stage);
  const closeModal = () => { setOpen(false); setForm(EMPTY); setExisting(null); };

  const reload = async () => {
    try {
      const { data } = await api.get("/customers", { params: { q, stage } });
      setItems(data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadPipe = async () => {
    try {
      const { data } = await api.get("/customers/pipeline");
      setPipeline(data.pipeline);
    } catch {}
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q, stage]);
  useEffect(() => { loadPipe(); }, []);

  const create = async () => {
    try {
      const { data } = await api.post("/customers", form);
      if (data.exists) { setExisting(data.customer); return; }
      toast.success(tr("x.customer_created"));
      closeModal();
      reload(); loadPipe();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="eyebrow">{tr("x.crm")}</div><h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("x.customers")}</h1></div>
        {can("customers", "create") && (
          <button data-testid="add-customer-btn" onClick={() => setOpen(true)} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold flex items-center gap-2"><Plus size={14} /> {tr("x.add_customer")}</button>
        )}
      </div>

      {pipeline.length > 0 && (
        <div className="panel p-4">
          <div className="eyebrow mb-3">{tr("x.pipeline_by_salesperson")}</div>
          <div className="overflow-x-auto">
          <table className="w-full dense-table pipeline-table" data-testid="pipeline-table">
            <thead><tr><th>{tr("x.salesperson")}</th>{STAGES.map((s) => <th key={s}>{lbl(s)}</th>)}<th>{tr("x.total")}</th><th>{tr("x.overdue")}</th></tr></thead>
            <tbody>
              {pipeline.map((p) => (
                <tr key={p.user_id} data-testid={`pipe-row-${p.user_id}`}>
                  <td className="font-semibold pipeline-name" data-testid={`pipe-name-${p.user_id}`}>{p.unassigned ? tr("x.pipe_unassigned") : p.user_name}{p.inactive ? ` ${tr("x.inactive_suffix")}` : ""}</td>
                  {STAGES.map((s) => <td key={s} className="tabular">{p.stages[s] || 0}</td>)}
                  <td className="tabular font-semibold">{p.total}</td>
                  <td className={p.overdue > 0 ? "tabular text-red-600 font-semibold" : "tabular"}>{p.overdue}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <div className="panel p-3 flex gap-2 flex-wrap">
        <input data-testid="customers-search" placeholder={tr("x.search_name_mobile_email_civil_id")} value={q} onChange={(e) => setQ(e.target.value)} className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm flex-1 min-w-[240px]" />
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
          <option value="">{tr("x.all_stages")}</option>
          {STAGES.map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
        </select>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr><th>{tr("x.name")}</th><th>{tr("x.mobile_2")}</th><th>{tr("x.email")}</th><th>{tr("x.type")}</th><th>{tr("x.source")}</th><th>{tr("x.stage")}</th><th>{tr("x.follow_up")}</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.mobile}`} className={isOverdue(c) ? "bg-red-50" : ""}>
                <td><Link to={`/customers/${c.id}`} className="font-semibold hover:text-accent-brand">{c.name}</Link></td>
                <td className="mono text-xs">{c.mobile}</td>
                <td className="text-xs">{c.email || "—"}</td>
                <td className="text-xs">{lbl(c.customer_type)}</td>
                <td className="text-xs">{lbl(c.source)}</td>
                <td><span className="pill pill-prep">{lbl(c.stage)}</span></td>
                <td className={isOverdue(c) ? "text-xs text-red-600 font-semibold" : "text-xs"} data-testid={isOverdue(c) ? `followup-overdue-${c.mobile}` : undefined}>{c.follow_up_date || "—"}{isOverdue(c) ? tr("x.overdue_2") : ""}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[var(--gray-400)]">{tr("x.no_customers_yet")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeModal}>
          <div className="panel p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="customer-modal">
            <div className="eyebrow mb-3">{tr("x.new_customer")}</div>
            <div className="space-y-3">
              <input data-testid="cust-name" placeholder={tr("x.full_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <input data-testid="cust-mobile" placeholder={tr("x.mobile")} value={form.mobile} onChange={(e) => { setForm({ ...form, mobile: e.target.value }); setExisting(null); }} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <input placeholder={tr("x.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <select value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                {["Buyer", "Consignment Owner", "Supplier", "Both"].map((t) => <option key={t} value={t}>{lbl(t)}</option>)}
              </select>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                {["Walk-in", "Website", "Live Chat", "WhatsApp", "Referral", "Social", "Phone"].map((t) => <option key={t} value={t}>{lbl(t)}</option>)}
              </select>
            </div>
            {existing && (
              <div className="mt-3 rounded-lg border border-amber-400 bg-amber-50 p-3 text-sm" data-testid="customer-exists-warning">
                <div className="font-semibold text-amber-800">{tr("x.customer_already_exists")} {existing.name} · {existing.mobile}</div>
                <Link to={`/customers/${existing.id}`} onClick={closeModal} className="btn-secondary mt-2 inline-flex" data-testid="open-existing-customer">{tr("x.open_existing_customer")}</Link>
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={closeModal} className="h-9 px-4 rounded-lg border text-sm" data-testid="cancel-customer">{tr("x.cancel")}</button>
              <button data-testid="submit-customer" onClick={create} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold">{tr("x.create")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
